import {
  ApiDateFormat,
  BaseModel,
} from '@kinet-open-network/client/shared/data-access/model';
import { StockItem } from '@kinet-open-network/client/stock-items/data-access';
import {
  AbilitySubjectType,
  DeviceState,
  DeviceType,
  DiscountType,
  getStockItemPriceTypeByDevicePossession,
  ICustomerDevice,
  IItemPrice,
  Possession,
} from '@kinet-open-network/shared/data-access/model';
import { Type } from 'class-transformer';
import { endOfDay } from 'date-fns';

export class CustomerDevice
  extends BaseModel<CustomerDevice, 'CustomerDevice'>
  implements ICustomerDevice
{
  static override readonly modelName: AbilitySubjectType = 'CustomerDevice';

  sn!: string | null;
  customerId!: number;
  typeId!: string;
  possession?: Possession;
  @Type(() => Number)
  price?: number;
  commitment?: boolean;
  @Type(() => Date)
  commitmentTo?: Date;
  state!: DeviceState;
  type!: DeviceType;
  apId!: string;
  isUsed?: boolean;
  discountType?: DiscountType;
  discountValue!: number | null;
  @ApiDateFormat('yyyy-MM-dd')
  @Type(() => Date)
  discountTo!: Date | null;
  @Type(() => Date)
  deliveryDate!: Date | null;
  @Type(() => Date)
  returnDate!: Date | null;
  @Type(() => Date)
  finePaidAt!: Date | null;
  invoiceId!: string | null;
  @Type(() => Date)
  invoiceDate!: Date | null;
  @Type(() => Date)
  invoiceFrom!: Date | null;
  @Type(() => Date)
  invoiceTo!: Date | null;
  note?: string;
  @Type(() => StockItem)
  stockItem?: StockItem;

  hasDiscountAt(date: Date | number = new Date()): boolean {
    return (
      !!this.discountValue &&
      (!this.discountTo || endOfDay(this.discountTo) > new Date(date))
    );
  }

  getPriceAt(date: Date | number = new Date()): number {
    const price = this.getActivePriceAt(date)?.price ?? 0;

    if (this.hasDiscountAt(date)) {
      return price - this.getDiscountAt(date);
    }

    return price;
  }

  getDiscountAt(date: Date | number = new Date()): number {
    if (!this.hasDiscountAt(date)) {
      return 0;
    }

    const priceValue = this.getActivePriceAt(date)?.price ?? 0;

    switch (this.discountType) {
      case DiscountType.Percentage:
        return (
          (priceValue * Math.min(100, Math.max(0, this.discountValue ?? 0))) /
          100
        );
      case DiscountType.Overwrite:
        // discountValue is the new overwritten price; discount = difference
        return Math.max(0, priceValue - (this.discountValue ?? 0));
      case DiscountType.Fixed:
        return Math.min(priceValue, this.discountValue ?? 0);
      default:
        return 0;
    }
  }

  getActivePriceAt(date: Date | number = new Date()): IItemPrice | null {
    const priceType = getStockItemPriceTypeByDevicePossession(this.possession);
    const price = this.stockItem?.prices?.find(
      (p) => p.type === priceType && p.isActiveAt(date),
    );

    return price ?? null;
  }

  get invoiced(): boolean {
    return !!this.invoiceDate;
  }

  get returned() {
    return this.state === DeviceState.returned;
  }
}
